import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {VehicleType} from '@service-and-repairs/awpintegrationlib';
import {Observable} from 'rxjs';
import {EnvironmentUrlKey} from '../../../interfaces/IEnvironment';
import {EtkConfiguration} from '../interfaces/etk-configuration';
import {OutletRelatedConfiguration} from '../interfaces/outlet-related-configuration';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private readonly httpClient: HttpClient = inject(HttpClient);

  static mapConfigVehicleType(vehicleType: VehicleType): string {
    if (vehicleType === VehicleType.PASSENGER_CAR) {
      return 'CAR';
    } else {
      return 'MOT';
    }
  }

  static mapEtkVehicleType(vehicleType: VehicleType): string {
    if (vehicleType === VehicleType.PASSENGER_CAR) {
      return 'PKW';
    } else {
      return 'MOTORRAD';
    }
  }

  static getUrlForPortalHost(key: EnvironmentUrlKey,
                             hostname: string = window.location.hostname): string {
    return ConfigurationService.usesIntranetUrls(hostname)
      ? environment.urls[key]?.b2dIntranet
      : environment.urls[key]?.b2d;
  }

  static usesIntranetUrls(hostname: string = window.location.hostname): boolean {
    return hostname.endsWith('.bmwgroup.net');
  }

  /**
   * Retrieve outlet configuration
   * See the Service Case Storage component for test and API spec:
   * awp-document-storage/src/test/rest/export-configuration.http
   * @return A promise with the config.
   */
  getOutletConfigFromBackend(businessPartnerId: string,
                             vehicleType: VehicleType): Promise<OutletRelatedConfiguration> {
    return new Promise<OutletRelatedConfiguration>((resolve, reject): void => {
      const configVehicleType: string = ConfigurationService.mapConfigVehicleType(vehicleType);
      this.makeHttpGetRequest(`/api/configuration/outlet/${businessPartnerId}/${configVehicleType}`)
        .subscribe({
          next: (response: any) => resolve(response.data),
          error: reject
        });
    });
  }

  /**
   * Retrieve ETK outlet configuration.
   * @return A promise with the config.
   */
  getEtkConfigFromBackend(businessPartnerId: string,
                          vehicleType: VehicleType): Promise<EtkConfiguration> {
    return new Promise<EtkConfiguration>((resolve, reject): void => {
      const etkConfigUrl: string = ConfigurationService.getUrlForPortalHost('etkConfig');
      const etkVehicleType: string = ConfigurationService.mapEtkVehicleType(vehicleType);
      this.makeHttpGetRequest<EtkConfiguration>(etkConfigUrl +
          `/etk-rest/outbound/configuration/outlet?businessPartnerId=${businessPartnerId}&vehicleType=${etkVehicleType}`)
        .subscribe({
          next: resolve,
          error: reject
        });
    });
  }

  private makeHttpGetRequest<T>(url: string): Observable<T> {
    return this.httpClient.get<T>(url, {withCredentials: true});
  }
}
